import React from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Link,
  Tooltip,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { formatSize, sinceLabel, splitExtension } from 'src/util/fileNames';
import { useAttachmentLinks } from './attachments';

// A record's attached files, one row each (TGCLOUD-1850).
//
// A file LIST, not chips: a chip reads as a tag that classifies the record,
// whereas these are documents you open. Each row is a type icon, the name as a
// real link (middle-click and open-in-new-tab work; a button inside a link
// silently does not navigate), and explicit Download / Remove actions.
//
// Long names truncate in the MIDDLE — the stem ellipsises and the extension
// stays — with the full name in a tooltip and as the link's accessible name.
// Done in CSS rather than by counting characters, so it uses whatever width the
// row actually has: the 850px record dialog and the narrower record card alike.

const IMAGE_EXTENSIONS = [
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
  'tif',
  'tiff',
  'heic',
  'heif',
  'svg'
];

export function FileTypeIcon({ name, sx }) {
  const ext = splitExtension(name).ext.slice(1).toLowerCase();
  const iconSx = { flexShrink: 0, color: 'text.secondary', ...sx };
  if (ext === 'pdf')
    return <PictureAsPdfOutlinedIcon fontSize="small" sx={iconSx} />;
  if (IMAGE_EXTENSIONS.includes(ext))
    return <ImageOutlinedIcon fontSize="small" sx={iconSx} />;
  return <DescriptionOutlinedIcon fontSize="small" sx={iconSx} />;
}

// Read by screen readers, not shown.
const visuallyHidden = {
  position: 'absolute',
  // Strings: in `sx` a bare 1 means 100%.
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0
};

// The name, shown stem-ellipsised with the extension kept. The full name is in
// the DOM once, visually hidden, so it is what assistive technology reads and
// what a text search finds; the visible split copy is aria-hidden.
export function FileName({ name, sx }) {
  const { stem, ext } = splitExtension(name);
  return (
    <Box
      component="span"
      sx={{
        display: 'flex',
        minWidth: 0,
        flex: 1,
        position: 'relative',
        ...sx
      }}
    >
      <Box component="span" sx={visuallyHidden}>
        {name}
      </Box>
      <Box
        component="span"
        aria-hidden="true"
        sx={{ display: 'flex', minWidth: 0, whiteSpace: 'nowrap' }}
      >
        <Box
          component="span"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}
        >
          {stem}
        </Box>
        {ext && (
          <Box component="span" sx={{ flexShrink: 0 }}>
            {ext}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// Re-render once a minute while any row says "n min ago", so it stays true.
function useMinuteTick(active) {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

export const attachmentRowSx = (dense) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minWidth: 0,
  px: 1.25,
  py: dense ? 0.25 : 0.5,
  minHeight: dense ? 34 : 40,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  bgcolor: 'background.paper',
  transition: 'background-color .15s',
  '&:hover': { bgcolor: 'action.hover' }
});

/**
 * @param {string[]} names       the record's fileAttachments
 * @param {string}   familyId    the record's familyMemberId (storage key prefix)
 * @param {object}   [knownLinks] name → signed URL already in hand
 * @param {object}   [details]   name → { size, uploadedAt } for files uploaded
 *                               in this session (older records hold only names)
 * @param {boolean}  [readOnly]  hide Remove
 * @param {function} [onRemove]  (name) => void
 * @param {boolean}  [dense]     tighter rows, for the record card
 */
export default function AttachmentList({
  names,
  familyId,
  knownLinks,
  details,
  readOnly,
  onRemove,
  dense = false,
  emptyText
}) {
  const list = (names || []).filter((n) => typeof n === 'string' && n);
  const { links, pending } = useAttachmentLinks(list, familyId, knownLinks);
  const hasRecent = list.some((n) => details?.[n]?.uploadedAt);
  const now = useMinuteTick(hasRecent);

  if (!list.length) {
    return emptyText ? (
      <Typography variant="body2" sx={{ color: 'text.secondary', py: 0.5 }}>
        {emptyText}
      </Typography>
    ) : null;
  }

  return (
    <Box
      component="ul"
      aria-label="Attached files"
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        minWidth: 0
      }}
    >
      {list.map((name, i) => {
        const href = links[name];
        const detail = details?.[name];
        const meta = detail
          ? [
              formatSize(detail.size),
              detail.uploadedAt ? sinceLabel(detail.uploadedAt, now) : ''
            ]
              .filter(Boolean)
              .join(' · ')
          : '';
        return (
          <Box component="li" key={`${name}-${i}`} sx={attachmentRowSx(dense)}>
            <FileTypeIcon name={name} />
            <Tooltip title={name} placement="top-start" enterDelay={400}>
              {href ? (
                <Link
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  underline="hover"
                  sx={{ display: 'flex', flex: 1, minWidth: 0 }}
                >
                  <FileName name={name} />
                </Link>
              ) : (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    display: 'flex',
                    flex: 1,
                    minWidth: 0,
                    color: 'text.disabled'
                  }}
                >
                  <FileName name={name} />
                </Typography>
              )}
            </Tooltip>
            {meta && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', flexShrink: 0 }}
              >
                {meta}
              </Typography>
            )}
            {/* Says WHY it cannot be opened, rather than looking clickable and doing nothing. */}
            {!href &&
              (pending ? (
                <CircularProgress
                  size={14}
                  sx={{ flexShrink: 0 }}
                  aria-label={`Finding ${name}`}
                />
              ) : (
                <Tooltip title="This file could not be found in storage">
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.disabled', flexShrink: 0 }}
                  >
                    unavailable
                  </Typography>
                </Tooltip>
              ))}
            {href && (
              <Tooltip title="Download">
                <IconButton
                  size="small"
                  component="a"
                  href={href}
                  download={name}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ flexShrink: 0 }}
                  aria-label={`Download ${name}`}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!readOnly && onRemove && (
              <Tooltip title="Remove (the file is deleted when you save)">
                <IconButton
                  size="small"
                  onClick={() => onRemove(name)}
                  sx={{ flexShrink: 0 }}
                  aria-label={`Remove ${name}`}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
